import { HTMLInputAutoCompleteAttribute, useCallback, useState } from "react";
import {
    ControllerRenderProps,
    FieldValues,
    Noop,
    Path,
    PathValue,
    useFormContext
} from "react-hook-form";
import { components, GroupBase, InputProps, MultiValue } from "react-select";

import { MenuComponent } from "@/components/Form/Base/components/selectMenu.component";
import { MenuListComponent } from "@/components/Form/Base/components/selectMenuList.component";
import { Select } from "@/components/Form/Base/select.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

export enum LabelType {
    DIVIDER = "DIVIDER"
}

export type BaseOption<T> = {
    label: string | LabelType;
    value?: T;
    isDisabled?: boolean;
};

type MultiSelectFieldProps<
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
> = FormValuesExploded<T> & {
    optionList: Array<TOption>;
    placeholder?: string;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    "aria-label"?: string;
};

const MultiSelectField = <
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
>({
    placeholder,
    autoComplete,
    optionList,
    disabled,
    "aria-label": ariaLabel,
    label,
    microCopy,
    name,
    required
}: MultiSelectFieldProps<T, TOption>) => {
    const { t } = useGranite();
    const { setValue, trigger } = useFormContext<T>();

    const [isFocussed, setIsFocussed] = useState(false);

    const getValue = (
        field: ControllerRenderProps<T, Path<T>>
    ): MultiValue<TOption> | undefined =>
        !isFocussed
            ? (optionList &&
                  field &&
                  ((field.value &&
                      optionList.filter((option) =>
                          field.value.includes(option.value)
                      )) || [optionList[0]])) ||
              undefined
            : undefined;

    const handleOnChange =
        (field: ControllerRenderProps<T, Path<T>>) =>
        (option: MultiValue<TOption>) => {
            setValue(
                field.name,
                (option ? option.map((v) => v.value) : null) as PathValue<
                    T,
                    Path<T>
                >,
                {
                    shouldValidate: false
                }
            );

            trigger(field.name);
        };

    const handleOnFocus = () => setIsFocussed(true);

    const handleOnBlur = (onBlurCb: Noop) => {
        setIsFocussed(false);
        onBlurCb();
    };

    const InputComponent: React.ComponentType<
        InputProps<TOption, true, GroupBase<TOption>>
    > = useCallback(
        (props) => (
            <components.Input
                {...props}
                autoComplete={autoComplete}
            />
        ),
        [autoComplete]
    );

    const renderOption = (option: TOption) => {
        if (option.label === LabelType.DIVIDER) {
            return <hr />;
        }

        return option.label;
    };

    return (
        <BaseField<T, Path<T>>
            label={label}
            microCopy={microCopy}
            name={name}
            required={required}
        >
            {({ field, fieldState }) => {
                return (
                    <Select<TOption, true>
                        {...field}
                        id={field.name}
                        instanceId={field.name}
                        openMenuOnFocus={true}
                        value={getValue(field)}
                        onChange={handleOnChange(field)}
                        isValid={fieldState.isValid}
                        classNamePrefix={"multi-select-field"}
                        placeholder={isFocussed ? "" : placeholder}
                        options={optionList}
                        isSearchable={true}
                        blurInputOnSelect={true}
                        onBlur={() => handleOnBlur(field.onBlur)}
                        onFocus={handleOnFocus}
                        isMulti={true}
                        components={{
                            Input: InputComponent,
                            Menu: MenuComponent,
                            MenuList: MenuListComponent
                        }}
                        noOptionsMessage={() =>
                            t("general.selectBox.no.options")
                        }
                        isDisabled={disabled}
                        aria-label={ariaLabel}
                        formatOptionLabel={renderOption}
                    />
                );
            }}
        </BaseField>
    );
};

export { MultiSelectField };
